<template>
  <div
    v-if="canRenderPlacement"
    ref="placementRef"
    class="iterable-embedded-placement"
    :class="{ 'iterable-embedded-placement--pending': !placementStructurallyReady }"
    :data-placement-id="normalizedPlacementId != null ? String(normalizedPlacementId) : ''"
    :aria-busy="!placementStructurallyReady"
    @click="onContainerClick"
  >
    <div
      v-for="(message, index) in messagesForPlacement"
      :key="embeddedMessageKey(message, index)"
      class="iterable-embedded-item"
      :data-message-id="dataMessageIdAttr(message, index)"
      :data-iterable-media-url="iterableMediaUrlAttr(message)"
      v-html="renderMessage(message)"
    />
  </div>
</template>

<script setup lang="ts">
import { IterableEmbeddedBanner, IterableEmbeddedCard, IterableEmbeddedNotification } from '@iterable/web-sdk'
import { computed, ref, watch, nextTick, onBeforeUnmount, toRaw } from 'vue'
import { useRoute } from 'vue-router'
import { useRuntimeConfig } from '#app'
import { useIterable } from '~/composables/useIterable'
import { logIterableError } from '~/utils/iterable-log'
import { applyIterablePlacementDom } from '~/utils/iterable-placement-dom'
import { STYLE_MAP, type MessageType } from '~/utils/iterable-placement-styles'
import {
  normalizeIterableBannerImageSide,
  normalizeIterableCampaignMessageType,
  safeStrapiTextField,
  unwrapStrapiScalar
} from '~/utils/iterable-live-merge'

/**
 * Runtime-safe placement id: Vue does not enforce `placementId: number` at runtime (CMS may send string).
 * Aligns map lookup with `embeddedMessagesByPlacement` numeric keys Iterable uses.
 */
function parsePositivePlacementId (raw: unknown): number | null {
  if (raw == null || raw === '') return null
  if (typeof raw === 'number') {
    if (!Number.isFinite(raw) || raw <= 0) return null
    return Math.trunc(raw)
  }
  const asText = safeStrapiTextField(unwrapStrapiScalar(raw), '')
  if (asText === '') return null
  const parsed = parseInt(asText, 10)
  if (!Number.isFinite(parsed) || parsed <= 0) return null
  return Math.trunc(parsed)
}

interface IterableEmbeddedMessage {
  metadata?: {
    messageId?: string
    placementId?: number
  }
  elements?: {
    title?: string
    body?: string
    mediaUrl?: string
    image?: string
    buttons?: Array<{ id?: string; btnText?: string; action?: { type?: string; data?: string } }>
  }
  data?: Record<string, unknown>
}

const props = withDefaults(
  defineProps<{
    placementId: number
    /** Message type (from Strapi or parent). Layout/styling only; message content always comes from Iterable. */
    messageType?: 'banner' | 'card' | 'notification'
    /** Banner only: desktop image side (`iterable-banner-parent` gets `image-position-left` | `image-position-right`). */
    bannerImageDesktopPlacement?: 'left' | 'right'
    /** @deprecated Use messageType. Kept for backward compatibility (e.g. default layout). */
    variant?: 'banner' | 'card' | 'notification'
    /** First CTA (Iterable’s first link/button in DOM order). */
    primaryButtonClass?: string
    /** Second CTA when present. */
    secondaryButtonClass?: string
    titleClass?: string
    bodyClass?: string
    /**
     * When true (e.g. Card-Programs grid), do not inject `card-program-item col-*` under `.iterable-card-parent`;
     * the parent already provides column + `.card-program-item`.
     */
    omitCardItemWrapper?: boolean
  }>(),
  {
    messageType: undefined,
    bannerImageDesktopPlacement: 'right',
    variant: 'banner',
    primaryButtonClass: 'btn btn-primary',
    secondaryButtonClass: 'btn btn-secondary',
    titleClass: 'heading-two',
    bodyClass: 'para-one',
    omitCardItemWrapper: false
  }
)

/** Finite integer placement id > 0, or null when the prop is missing/invalid at runtime. */
const normalizedPlacementId = computed(() => parsePositivePlacementId(props.placementId as unknown))

const route = useRoute()
const runtimeConfig = useRuntimeConfig()

/**
 * How often to re-try DOM apply when `placementRef` is not mounted yet (nextTick + rAF each time).
 * Env: `ITERABLE_PLACEMENT_DOM_APPLY_MAX_RETRIES` → `public.iterablePlacementDomApplyMaxRetries`.
 * Invalid or less than 1 defaults to **10**; values are floored and capped at **50** to avoid unbounded work.
 */
const maxPlacementDomApplyRetries = (() => {
  const raw = Number(runtimeConfig.public.iterablePlacementDomApplyMaxRetries)
  if (!Number.isFinite(raw) || raw < 1) return 10
  return Math.min(50, Math.floor(raw))
})()

/** Stable Vue keys when messageId is missing or duplicated */
function embeddedMessageKey (message: IterableEmbeddedMessage, index: number): string {
  const metadataMessageId = message?.metadata?.messageId
  const placementKey = normalizedPlacementId.value ?? 'invalid'
  const idPart = safeStrapiTextField(unwrapStrapiScalar(metadataMessageId as unknown), '')
  return `${placementKey}-${idPart !== '' ? idPart : `idx-${index}`}`
}

/** Analytics / DOM fallback when Iterable omits messageId */
function fallbackMessageId (index: number): string {
  const placementKey = normalizedPlacementId.value ?? 'invalid'
  return `placement-${placementKey}-${index}`
}

function dataMessageIdAttr (message: IterableEmbeddedMessage, index: number): string {
  const id = safeStrapiTextField(unwrapStrapiScalar(message?.metadata?.messageId as unknown), '')
  return id !== '' ? id : fallbackMessageId(index)
}

/**
 * `IterableEmbeddedNotification` often omits `<img>` even when `elements.mediaUrl` is set.
 * Expose URL on the item wrapper so `applyIterablePlacementDom` can inject an image when needed.
 */
function iterableMediaUrlForItem (message: IterableEmbeddedMessage): string | undefined {
  const raw = message?.elements?.mediaUrl ?? message?.elements?.image
  if (raw == null) return undefined
  const trimmedMediaUrl = safeStrapiTextField(unwrapStrapiScalar(raw), '')
  return trimmedMediaUrl !== '' ? trimmedMediaUrl : undefined
}

/** String only — `data-*` must not receive objects (Vue `patchAttr` / ToPrimitive). */
function iterableMediaUrlAttr (message: IterableEmbeddedMessage): string {
  const mediaUrlOrEmpty = iterableMediaUrlForItem(message)
  return mediaUrlOrEmpty != null && mediaUrlOrEmpty !== '' ? mediaUrlOrEmpty : ''
}

const resolvedMessageType = computed<MessageType>(() =>
  normalizeIterableCampaignMessageType(props.messageType ?? props.variant) as MessageType
)

/** Strapi enum / stray objects must never reach DOM `className` or Vue class normalization. */
const primaryButtonClassSafe = computed(() =>
  safeStrapiTextField(unwrapStrapiScalar(props.primaryButtonClass), '')
)
const secondaryButtonClassSafe = computed(() =>
  safeStrapiTextField(unwrapStrapiScalar(props.secondaryButtonClass), '')
)
const titleClassSafe = computed(() => safeStrapiTextField(unwrapStrapiScalar(props.titleClass), ''))
const bodyClassSafe = computed(() => safeStrapiTextField(unwrapStrapiScalar(props.bodyClass), ''))
const bannerImageDesktopSideSafe = computed(() =>
  normalizeIterableBannerImageSide(props.bannerImageDesktopPlacement)
)

const {
  isEnabled,
  embeddedManager,
  embeddedMessagesByPlacement,
  packageName,
  startImpression,
  pauseImpression,
  identityReady,
  hasIterableIdentity
} = useIterable()

const placementIdValid = computed(() => normalizedPlacementId.value != null)

const messagesForPlacement = computed(() => {
  if (!isEnabled.value || !embeddedMessagesByPlacement?.value) return []
  const placementKey = normalizedPlacementId.value
  if (placementKey == null) return []
  const byPlacement = toRaw(embeddedMessagesByPlacement.value) as Record<number, IterableEmbeddedMessage[]>
  const list = byPlacement[placementKey]
  return Array.isArray(list) ? list : []
})

/**
 * Avoid mounting / v-html until SDK init finished and we have Iterable identity (email/userId).
 * Prevents racing getMessages before JWT + setEmail completes.
 *
 * `identityReady` / `hasIterableIdentity` are always `Ref<boolean>` from `useIterable()` (no optional chaining on the refs).
 */
const canRenderPlacement = computed(() => {
  if (!isEnabled.value || !placementIdValid.value) return false
  if (!identityReady.value || !hasIterableIdentity.value) return false
  return (messagesForPlacement.value?.length ?? 0) > 0
})

/**
 * The placement `<div>` is behind `v-if="canRenderPlacement"`; the component can stay mounted while
 * that flips false (e.g. messages cleared). Pending `nextTick` / `rAF` must no-op in that window — same
 * race as full unmount (`placementDomTeardown`) for `Cannot read properties of null (reading 'exposed')`.
 */
function placementDomWorkAllowed (): boolean {
  return !placementDomTeardown && canRenderPlacement.value
}

const placementRef = ref<HTMLElement | null>(null)
/** Hide Iterable's default markup until our first DOM pass completes (per placement). */
const placementStructurallyReady = ref(false)
let mutationObserver: MutationObserver | null = null
let applyScheduled = false
/**
 * Set to `true` in `onBeforeUnmount` (synchronous). Every queued `nextTick` / `rAF` callback
 * re-checks `placementDomWorkAllowed()` — which reads this flag — before doing any DOM work,
 * so no callback touches the DOM after the component is torn down even during fast navigation.
 * Also covers the narrower case where only `v-if="canRenderPlacement"` drops the inner root
 * while the component itself stays mounted (e.g. messages cleared mid-session).
 */
let placementDomTeardown = false

/** Reveal after layout so we don't flash unstyled HTML (extra rAF for banner so flex/image layout can settle). */
function scheduleRevealAfterLayout () {
  if (!placementDomWorkAllowed()) return
  if (placementStructurallyReady.value) return

  const prefersReduced =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches

  if (prefersReduced) {
    if (!placementDomWorkAllowed()) return
    placementStructurallyReady.value = true
    return
  }

  const reveal = () => {
    if (!placementDomWorkAllowed()) return
    placementStructurallyReady.value = true
  }

  requestAnimationFrame(() => {
    if (!placementDomWorkAllowed()) return
    requestAnimationFrame(() => {
      if (!placementDomWorkAllowed()) return
      if (resolvedMessageType.value === 'banner') {
        requestAnimationFrame(reveal)
      } else {
        reveal()
      }
    })
  })
}

function scheduleApplyCustomClasses () {
  if (!placementDomWorkAllowed()) return
  if (applyScheduled) return
  applyScheduled = true
  requestAnimationFrame(() => {
    applyScheduled = false
    if (!placementDomWorkAllowed()) return
    applyCustomClasses()
  })
}

function stopMutationObserver () {
  if (mutationObserver) {
    mutationObserver.disconnect()
    mutationObserver = null
  }
}

function startMutationObserver (root: HTMLElement) {
  if (!placementDomWorkAllowed()) return
  if (mutationObserver) return
  mutationObserver = new MutationObserver(() => {
    scheduleApplyCustomClasses()
  })
  mutationObserver.observe(root, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['style']
  })
}

function applyCustomClasses (rootRetry = 0) {
  if (!placementDomWorkAllowed()) return
  const root = placementRef.value
  if (!root) {
    if (rootRetry < maxPlacementDomApplyRetries) {
      nextTick(() => {
        if (!placementDomWorkAllowed()) return
        requestAnimationFrame(() => {
          if (!placementDomWorkAllowed()) return
          applyCustomClasses(rootRetry + 1)
        })
      })
    } else if (placementDomWorkAllowed()) {
      scheduleRevealAfterLayout()
    }
    return
  }

  try {
    const messageType = resolvedMessageType.value
    const styles = STYLE_MAP[messageType]
    const buttonClasses = [primaryButtonClassSafe.value, secondaryButtonClassSafe.value]
    const titleClass = titleClassSafe.value || styles.title
    const bodyClass = bodyClassSafe.value || styles.body

    applyIterablePlacementDom(root, {
      messageType,
      /** Banner-only; omit for card/notification so placement options never affect non-banner paths. */
      bannerImageDesktopPlacement:
        messageType === 'banner' ? bannerImageDesktopSideSafe.value : undefined,
      omitCardItemWrapper: props.omitCardItemWrapper,
      buttonClasses,
      titleClass,
      bodyClass,
      stopMutationObserver,
      resumeMutationObserver: () => {
        nextTick(() => {
          if (!placementDomWorkAllowed()) return
          const placementRootEl = placementRef.value
          if (placementRootEl) startMutationObserver(placementRootEl)
        })
      },
      logError: (ctx, err) => logPlacementError(ctx, err)
    })
    if (!mutationObserver && placementDomWorkAllowed()) {
      startMutationObserver(root)
    }
  } catch (err) {
    logPlacementError('applyCustomClasses', err)
  } finally {
    if (placementDomWorkAllowed()) {
      scheduleRevealAfterLayout()
    }
  }
}

function logPlacementError (context: string, err: unknown) {
  logIterableError(context, err, {
    placementId: normalizedPlacementId.value ?? props.placementId,
    placementIdRaw: props.placementId,
    route: route?.fullPath != null ? String(route.fullPath) : ''
  })
}

function renderMessage (message: IterableEmbeddedMessage): string {
  if (!packageName || !message) return ''
  try {
    const opts = {
      appPackageName: packageName,
      message,
      errorCallback: (err: unknown) => logPlacementError('Iterable embedded render', err)
    }
    const optsAsSdk = opts as Parameters<typeof IterableEmbeddedBanner>[0]
    const layoutMessageType = resolvedMessageType.value
    if (layoutMessageType === 'card') return IterableEmbeddedCard(optsAsSdk)
    if (layoutMessageType === 'notification') return IterableEmbeddedNotification(optsAsSdk)
    return IterableEmbeddedBanner(optsAsSdk)
  } catch (err) {
    logPlacementError('renderMessage', err)
    return ''
  }
}

function onContainerClick (event: MouseEvent) {
  const manager = embeddedManager as { click?: (href: string) => void } | null | undefined
  if (manager == null || typeof manager.click !== 'function') return
  try {
    const root = event.currentTarget
    if (!(root instanceof HTMLElement)) return
    const target = event.target
    if (!(target instanceof Node) || !root.contains(target)) return
    const link = (target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null
    if (!link?.href || !root.contains(link)) return
    /** Only Iterable message HTML under `.iterable-embedded-item` — never hijack unrelated links. */
    const item = link.closest('.iterable-embedded-item')
    if (!(item instanceof HTMLElement) || !root.contains(item)) return
    event.preventDefault()
    const hrefRaw = link.getAttribute('href')?.trim()
    const href = hrefRaw !== '' && hrefRaw != null ? hrefRaw : link.href
    if (!href.trim()) return
    manager.click(href)
  } catch (err) {
    logPlacementError('onContainerClick', err)
  }
}

const trackedImpressions = new Set<string>()

/**
 * Only reset "pending" when switching placements — never on every SDK message mutation
 * (deep updates were toggling opacity and caused flicker + blank gaps).
 * Clear impression bookkeeping when `placementId` changes so the new placement can track correctly.
 *
 * Must be passed directly as `watch`’s callback (second argument). Do not wrap in `() => { … }`
 * or the inner `(newId, oldId) => …` would never run.
 */
function onPlacementIdChange (newId: number | null, oldId: number | null | undefined) {
  /** Avoid spurious clear on first tick; reset when normalized placement changes (including valid → invalid). */
  if (oldId != null && oldId !== newId) {
    trackedImpressions.forEach((impressionMessageId) => pauseImpression(impressionMessageId))
    trackedImpressions.clear()
  }
  placementStructurallyReady.value = false
}

watch(normalizedPlacementId, onPlacementIdChange)

watch(
  messagesForPlacement,
  (messages) => {
    if (!placementDomWorkAllowed()) return
    messages.forEach((msg: IterableEmbeddedMessage) => {
      if (!msg?.metadata?.messageId) return
      const realMessageId = safeStrapiTextField(msg.metadata.messageId as unknown, '')
      if (!realMessageId) return
      const placementIdFromMeta = msg?.metadata?.placementId
      const placementIdForImpression =
        parsePositivePlacementId(placementIdFromMeta) ?? normalizedPlacementId.value
      if (placementIdForImpression == null) return
      if (!trackedImpressions.has(realMessageId)) {
        trackedImpressions.add(realMessageId)
        startImpression(realMessageId, placementIdForImpression)
      }
    })
  },
  { immediate: true }
)

watch(
  [
    /** Include so when identity flips on after messages were already loaded, we still run DOM apply once the root exists. */
    canRenderPlacement,
    messagesForPlacement,
    resolvedMessageType,
    bannerImageDesktopSideSafe,
    () => props.omitCardItemWrapper,
    primaryButtonClassSafe,
    secondaryButtonClassSafe,
    titleClassSafe,
    bodyClassSafe
  ],
  () => {
    if (!canRenderPlacement.value) {
      placementStructurallyReady.value = false
      stopMutationObserver()
      applyScheduled = false
      return
    }
    /** Hide until DOM pass runs again so layout/messageType changes don’t leave a half-styled banner visible. */
    placementStructurallyReady.value = false
    nextTick(() => {
      if (!placementDomWorkAllowed()) return
      applyCustomClasses()
      const root = placementRef.value
      if (root && !mutationObserver) startMutationObserver(root)
    })
  },
  { immediate: true }
)

onBeforeUnmount(() => {
  placementDomTeardown = true
  applyScheduled = false
  stopMutationObserver()
  trackedImpressions.forEach((impressionMessageId) => pauseImpression(impressionMessageId))
  trackedImpressions.clear()
})
</script>

<style lang="scss">
/* Common styles for all message types */
.iterable-embedded-placement {
  position: relative;
  transition: opacity 0.2s ease;
}

@media (prefers-reduced-motion: reduce) {
  .iterable-embedded-placement {
    transition: none;
  }
}

/**
 * Hide until first reveal (visibility + opacity avoids some paint glitches vs opacity alone).
 * No placeholder/skeleton.
 */
.iterable-embedded-placement--pending {
  visibility: hidden;
  opacity: 0;
  pointer-events: none;
}

.iterable-embedded-placement:not(.iterable-embedded-placement--pending) {
  visibility: visible;
}

.iterable-embedded-placement {
  .heading-two,
  .heading-three {
    font-size: 1.75rem;
    font-weight: 600;
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
    letter-spacing: normal;
    margin-top: 0;
    margin-bottom: 0.5rem;
    font-family: inherit;
    @media (min-width: 768px) {
      font-size: 2rem;
    }
  }
  .heading-three {
    font-size: 1.5rem;
    @media (min-width: 1200px) {
      font-size: 1.5rem;
    }
    @media (min-width: 768px) {
      font-size: 1.75rem;
    }
    
  }
  .para-one {
    font-size: 1rem;
    margin-bottom: 1rem;
  }

  .heading-two,
  .heading-three,
  .para-one {
    color: $color-primary;
    display: flex;
  }
}
.iterable-embedded-item :deep(a) {
  cursor: pointer;
}

.iterable-banner-parent,
.iterable-card-parent,
.iterable-notification-parent {
  button {
    &.btn:not(:first-child) {
      margin-left: 0.5rem;
    }
  }
}

/* Banner */
.iterable-banner-parent {
  display: flex;
  overflow: visible;
  @media screen and (max-width: 800px) {
    flex-direction: column;
    min-width: fit-content;
  }
  .banner-image {
    display: block;
  }
}

/* Strapi “Image desktop placement”: swap image / text columns at md+ (Bootstrap order-* utilities). */
@media (min-width: 768px) {
  .iterable-banner-parent.image-position-left {
    /* Override existing !important order styles from Bootstrap  */
    .iterable-image {
      order: 1 !important;
    }
    .iterable-content-wrapper {
      order: 2 !important;
    }
  }
}

/* Card */
.iterable-card-parent {
  justify-content: center;
  display: flex;
  .iterable-image {
    .card-image {
      border-radius: 12px;
      display: block;
      margin: 0 auto;
      max-width: 100%;
    }
  }
  .iterable-content {
    display: block;
    margin-top: 20px;
    padding: 0 20px;
    text-align: center;
    & * {
      justify-content: center;
    }
  }
  .iterable-button-parent {
    text-align: center;
    button {
      width: 100%;
      &.btn:not(:first-child) {
        margin-left: unset;
        margin-top: 1rem;
      }
    }
  }
}
.card-program-item {
 .iterable-card-parent {
  flex-direction: column;
  .iterable-content {
    padding: 0;
  }
  .btn {
    font-weight: normal;
  }
 }
}

/* Notification */
.iterable-notification-parent {
  display: flex;
  flex-direction: row;
  @media screen and (max-width: 800px) {
    flex-direction: column;
    min-width: fit-content;
  }
  .notification-title,
  .notification-body {
    color: inherit;
  }
  .iterable-image {
    flex: 0 0 auto;
    width: 50%;
    .notification-image {
      width: 100%;
    }
  }
}
</style>
